import { useCallback } from 'react';
import { isSafeRedirectTarget, type ReauthPurpose, type StepUpChallenge } from '@/api';
import {
  type ConnectedAccountsApi,
  clearPendingFlow,
  readPendingFlow,
  writePendingFlow,
} from './types';

/**
 * Outcome of a single dispatch step. `redirecting` and `email_pending` are
 * terminal for the current page load — the browser is leaving or the user must
 * check their inbox. `completed` means the flow finished in-place (the Nostr
 * path can complete without a round-trip).
 */
export type FlowStep =
  | { kind: 'redirecting' }
  | { kind: 'email_pending' }
  | { kind: 'nostr_sign_required'; sign: () => Promise<FlowStep> }
  | { kind: 'completed' }
  | { kind: 'deleted' }
  | { kind: 'error'; code: string };

type Nip07 = { signEvent: (e: unknown) => Promise<unknown> };

/**
 * NIP-07 extensions inject `window.nostr` asynchronously, so right after the
 * OIDC redirect return it may not be present yet. Poll briefly before failing.
 */
async function waitForNostr(timeoutMs = 4000): Promise<Nip07 | null> {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    const nostr = (window as unknown as { nostr?: Nip07 }).nostr;
    if (nostr?.signEvent) return nostr;
    if (Date.now() >= deadline) return null;
    await new Promise((r) => setTimeout(r, 120));
  }
}

async function signNostrChallenge(challenge: string): Promise<unknown> {
  const nostr = await waitForNostr();
  if (!nostr) {
    throw new Error('nostr_extension_missing');
  }
  return nostr.signEvent({
    kind: 22242,
    created_at: Math.floor(Date.now() / 1000),
    tags: [['challenge', challenge]],
    content: '',
  });
}

function redirectTo(authUrl: string): FlowStep {
  if (!isSafeRedirectTarget(authUrl)) {
    return { kind: 'error', code: 'unsafe_redirect' };
  }
  window.location.assign(authUrl);
  return { kind: 'redirecting' };
}

export interface UseConnectedAccountsFlowResult {
  /** Begin linking a new SSO identity (redirects to the OIDC provider). */
  startLink: () => Promise<FlowStep>;
  /** Begin unlinking identity `id` (step-up then DELETE). */
  startUnlink: (id: string) => Promise<FlowStep>;
  /** Begin deleting the account (step-up then DELETE /account). */
  startDelete: (confirmation: string) => Promise<FlowStep>;
  /**
   * Resume a flow after a redirect leg lands on `#/account/linked`. Pass the
   * parsed landing params. Returns the resulting step so the landing screen can
   * render the right message.
   */
  resumeFromLanding: (params: {
    status: string | null;
    proof?: string | null;
    stepup?: string | null;
    error?: string | null;
  }) => Promise<FlowStep>;
}

/**
 * Owns the multi-leg link/unlink state machine. Persistence lives in
 * `sessionStorage` (see `types.ts`) so it survives the OIDC / email round
 * trips; this hook is pure orchestration over the injected `api`.
 */
export function useConnectedAccountsFlow(
  api: ConnectedAccountsApi
): UseConnectedAccountsFlowResult {
  /**
   * Dispatch a step-up challenge. For redirect methods the browser leaves; for
   * nostr we sign + verify and hand back the `step_up_token`; for email we stop
   * and wait for the emailed link. `onStepUpToken` runs only on the in-place
   * (nostr) path.
   */
  const dispatchStepUp = useCallback(
    async (
      purpose: ReauthPurpose,
      challenge: StepUpChallenge,
      onStepUpToken: (token: string) => Promise<FlowStep>
    ): Promise<FlowStep> => {
      const method = challenge.method.toLowerCase();

      if (challenge.auth_url) {
        return redirectTo(challenge.auth_url);
      }

      if (method === 'nostr') {
        if (!challenge.challenge) {
          return { kind: 'error', code: 'step_up_invalid' };
        }
        // NIP-07 signing needs a user gesture and the extension may inject late
        // after the redirect — surface a button instead of auto-signing.
        const nostrChallenge = challenge.challenge;
        return {
          kind: 'nostr_sign_required',
          sign: async () => {
            try {
              const signed = await signNostrChallenge(nostrChallenge);
              const { step_up_token } = await api.reauthNostrVerify(purpose, signed);
              return onStepUpToken(step_up_token);
            } catch (e) {
              return {
                kind: 'error',
                code: e instanceof Error ? e.message : 'step_up_invalid',
              };
            }
          },
        };
      }

      if (method === 'email_magic_link' || challenge.email_sent) {
        return { kind: 'email_pending' };
      }

      return { kind: 'error', code: 'step_up_invalid' };
    },
    [api]
  );

  const startLink = useCallback(async (): Promise<FlowStep> => {
    const { auth_url } = await api.linkOidcInitiate();
    writePendingFlow({ action: 'link' });
    return redirectTo(auth_url);
  }, [api]);

  const startUnlink = useCallback(
    async (id: string): Promise<FlowStep> => {
      writePendingFlow({ action: `unlink:${id}` });
      const challenge = await api.reauthInitiate('unlink');
      return dispatchStepUp('unlink', challenge, async (token) => {
        await api.unlinkIdentity(id, token);
        clearPendingFlow();
        return { kind: 'completed' };
      });
    },
    [api, dispatchStepUp]
  );

  const startDelete = useCallback(
    async (confirmation: string): Promise<FlowStep> => {
      writePendingFlow({ action: 'delete' });
      const challenge = await api.reauthInitiate('delete');
      return dispatchStepUp('delete', challenge, async (token) => {
        await api.deleteAccount(confirmation, token);
        clearPendingFlow();
        return { kind: 'deleted' };
      });
    },
    [api, dispatchStepUp]
  );

  const resumeFromLanding = useCallback(
    async (params: {
      status: string | null;
      proof?: string | null;
      stepup?: string | null;
      error?: string | null;
    }): Promise<FlowStep> => {
      const { status } = params;

      if (status === 'error') {
        clearPendingFlow();
        return { kind: 'error', code: params.error || 'unknown' };
      }

      // Leg 1 return: the OIDC link round-trip produced a link-proof token.
      // Persist it, then begin step-up re-auth against the PRIMARY method.
      if (status === 'proof') {
        if (!params.proof) {
          return { kind: 'error', code: 'missing_proof' };
        }
        writePendingFlow({ action: 'link', proof: params.proof });
        const challenge = await api.reauthInitiate('link');
        return dispatchStepUp('link', challenge, async (token) => {
          await api.linkCommit(params.proof as string, token);
          clearPendingFlow();
          return { kind: 'completed' };
        });
      }

      // Leg 2 return: a redirect/email step-up produced a step-up token. Finish
      // whichever action was pending.
      if (status === 'stepup') {
        if (!params.stepup) {
          return { kind: 'error', code: 'missing_stepup' };
        }
        const pending = readPendingFlow();
        if (!pending) {
          return { kind: 'error', code: 'no_pending_flow' };
        }
        if (pending.action === 'link') {
          if (!pending.proof) {
            clearPendingFlow();
            return { kind: 'error', code: 'missing_proof' };
          }
          await api.linkCommit(pending.proof, params.stepup);
          clearPendingFlow();
          return { kind: 'completed' };
        }
        if (pending.action.startsWith('unlink:')) {
          const id = pending.action.slice('unlink:'.length);
          await api.unlinkIdentity(id, params.stepup);
          clearPendingFlow();
          return { kind: 'completed' };
        }
        if (pending.action === 'delete') {
          // Confirmation was validated before the redirect leg; "DELETE" is the
          // literal the backend requires.
          await api.deleteAccount('DELETE', params.stepup);
          clearPendingFlow();
          return { kind: 'deleted' };
        }
        clearPendingFlow();
        return { kind: 'error', code: 'no_pending_flow' };
      }

      return { kind: 'error', code: 'unknown' };
    },
    [api, dispatchStepUp]
  );

  return { startLink, startUnlink, startDelete, resumeFromLanding };
}
